
/**
 * Assessment Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { ApiError } from '../errors/api-error.js';
import { withRetry } from '../utils/retry.js';

export class AssessmentService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                meta: response.meta || {},
                pagination: response.pagination,
                uniqueValues: response.uniqueValues,
                message: response.message || 'Assessment listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch assessment list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch assessment');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const payload = data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Assessment oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create assessment');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const payload = data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Assessment güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update assessment');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Assessment silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete assessment');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        const results = { success: [], failed: [] };

        for (const id of ids) {
            try {
                await this.api.delete(id);
                results.success.push(id);
            } catch (error) {
                results.failed.push({ id, error: error.message });
            }
        }

        return {
            success: results.failed.length === 0,
            data: results,
            message: `${results.success.length}/${ids.length} öğe silindi`
        };
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    async getAuditControls(id) {
        try {
            const response = await withRetry(
                () => this.api.getAuditControls(id),
                { maxRetries: 2 }
            );
            const body = response.data || response;
            return {
                success: true,
                data: body.data,
                message: body.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch audit controls');
        }
    }

    async getEvaluation(id, params = {}) {
        try {
            const response = await this.api.getEvaluation(id, params);
            const body = response.data || response;
            return {
                success: true,
                data: body.data,
                message: body.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch assessment evaluation');
        }
    }

    async submitDraft(id, data) {
        try {
            const response = await this.api.submitDraft(id, data);
            const body = response.data || response;
            return {
                success: true,
                data: body.data,
                message: body.message || 'Draft saved successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to save draft');
        }
    }

    async submitEvaluation(id, data) {
        try {
            const response = await this.api.submitEvaluation(id, data);
            const body = response.data || response;
            return {
                success: true,
                data: body.data,
                message: body.message || 'Evaluation submitted successfully'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to submit evaluation');
        }
    }

    async getAllEvaluations(id) {
        try {
            const response = await withRetry(
                () => this.api.getAllEvaluations(id),
                { maxRetries: 2 }
            );
            const body = response.data || response;
            return {
                success: true,
                data: body.data,
                message: body.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch all evaluations');
        }
    }

    async getAllAuditControls(id) {
        try {
            const response = await withRetry(
                () => this.api.getAllAuditControls(id),
                { maxRetries: 2 }
            );
            const body = response.data || response;
            return {
                success: true,
                data: body.data,
                message: body.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch all audit controls');
        }
    }

    async uploadControlEvidence(assessmentId, controlId, file, optionsOrCallback) {
        try {
            const response = await this.api.uploadControlEvidence(assessmentId, controlId, file, optionsOrCallback);
            const body = response.data || response;
            return {
                success: true,
                data: body.data,
                message: body.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to upload control evidence');
        }
    }

    async deleteControlEvidence(assessmentId, controlId, storedName) {
        try {
            await this.api.deleteControlEvidence(assessmentId, controlId, storedName);
            return { success: true };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete control evidence');
        }
    }

    async downloadControlEvidence(assessmentId, controlId, storedName) {
        try {
            const response = await this.api.downloadControlEvidence(assessmentId, controlId, storedName);
            const blobLike = response?.data ?? response;
            if (blobLike instanceof Blob) return blobLike;
            throw new ApiError('Invalid binary payload for control evidence download', 500, 'INVALID_BINARY_PAYLOAD', {
                payloadType: typeof blobLike,
                hasDataField: !!response?.data
            });
        } catch (error) {
            throw this._handleError(error, 'Failed to download control evidence');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
